import { stats } from "../data/mock";
import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";

const AnimatedCounter = ({ value, suffix = "", duration = 2000 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [count, setCount] = useState(0);

  // Parse numeric value
  const numericStr = value.replace(/[^0-9.]/g, "");
  const target = parseFloat(numericStr) || 0;
  const prefix = value.match(/^[^0-9]*/)?.[0] || "";
  const afterSuffix = value.replace(numericStr, "").replace(prefix, "") || suffix;
  const isDecimal = numericStr.includes(".");

  useEffect(() => {
    if (!isInView) return;

    let startTime;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(eased * target);

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);
  }, [isInView, target, duration]);

  const displayValue = isDecimal
    ? count.toFixed(2)
    : Math.round(count).toString();

  return (
    <span ref={ref}>
      {prefix}{displayValue}{afterSuffix}
    </span>
  );
};

export const StatsSection = () => {
  return (
    <section
      className="relative w-full"
      style={{
        background: "#0F1225",
        borderTop: "1px solid rgba(123,104,238,0.1)",
        borderBottom: "1px solid rgba(123,104,238,0.1)",
        padding: "80px 7.6923%",
      }}
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="text-center md:text-left"
              style={{
                borderLeft: index > 0 ? "1px solid rgba(123,104,238,0.12)" : "none",
                paddingLeft: index > 0 ? "32px" : "0",
              }}
            >
              <div
                style={{
                  fontFamily: "'Kode Mono', monospace",
                  fontSize: "clamp(36px, 4vw, 56px)",
                  fontWeight: 600,
                  color: "#7B68EE",
                  lineHeight: 1.1,
                  marginBottom: "8px",
                }}
              >
                <AnimatedCounter value={stat.value} duration={2200 + index * 200} />
              </div>
              <div
                style={{
                  fontFamily: "'Kode Mono', monospace",
                  fontSize: "14px",
                  color: "#8585A8",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
